import { ThemePrefSchema } from "@uzverse/contracts";
import type { ThemePref } from "@uzverse/contracts";
import { adoptThemePreference, getThemePreference, onThemeSave } from "@uzverse/ui";

/**
 * The UI theme as a user setting on the gateway (`PATCH /api/v1/settings { theme }`), so it follows
 * the user across devices. localStorage (`uz.theme`, read by the inline script in index.html) is the
 * instant cache; the server value wins once it arrives, and a choice only this device has is
 * pushed up.
 */
export interface ThemeTransport {
  get(): Promise<ThemePref | null>;
  put(pref: ThemePref): Promise<void>;
}

const SETTINGS_URL = "/api/v1/settings";

export const httpTheme: ThemeTransport = {
  async get() {
    const res = await fetch(SETTINGS_URL, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`settings: ${res.status}`);
    const body = (await res.json()) as { settings?: { theme?: unknown } };
    const parsed = ThemePrefSchema.safeParse(body.settings?.theme);
    return parsed.success ? parsed.data : null;
  },
  async put(theme) {
    const res = await fetch(SETTINGS_URL, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ theme }),
    });
    if (!res.ok) throw new Error(`settings: ${res.status}`);
  },
};

export function createMemoryTheme(initial: ThemePref | null = null): ThemeTransport {
  let data = initial;
  return {
    get: () => Promise.resolve(data),
    put: (pref) => {
      data = pref;
      (globalThis as { __uzMockTheme?: ThemePref }).__uzMockTheme = pref;
      return Promise.resolve();
    },
  };
}

function defaultTransport(): ThemeTransport | null {
  if (import.meta.env.MODE === "test") return null;
  if (import.meta.env["VITE_UZ_MOCK"] === "1") return createMemoryTheme();
  return httpTheme;
}

let transport: ThemeTransport | null = defaultTransport();
let unhook: (() => void) | null = null;

/** Tests inject a transport (or null to switch the server side off). */
export function setThemeTransport(next: ThemeTransport | null): void {
  transport = next;
}

/**
 * Starts saving the user's theme changes and reads the saved one once (call after sign-in).
 * Returns a stop function. A failed save only loses the sync: the cache already holds the value.
 */
export async function syncTheme(): Promise<void> {
  unhook?.();
  unhook = onThemeSave((pref) => void transport?.put(pref).catch(() => undefined));
  if (!transport) return;
  try {
    const remote = await transport.get();
    if (remote) adoptThemePreference(remote);
    else {
      const local = getThemePreference();
      if (local !== "system") await transport.put(local);
    }
  } catch {
    // Offline: the cached choice stays.
  }
}

export function stopThemeSync(): void {
  unhook?.();
  unhook = null;
}
